/**
 * Two: Choose Services
 */
/* eslint-disable @next/next/no-img-element */
import React, { useState, useEffect, useRef, forwardRef } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import _, { groupBy, map, keyBy, sum } from 'lodash'
import {
  mq,
  useDayViewBooking,
  trackViewServiceList,
  trackSelectService,
  servicesUnavailableInMarket,
  trackBookingLocationOpened,
  trackBookingMarketChanged,
  trackBookingLocationDismissed,
  trackBookingServiceFilterChanged,
  trackBookingServiceModalClosed,
  trackPriceNotificationDismissed,
  hasSeenPriceNotification,
  markPriceNotificationSeen,
} from '../../../helpers'

import MarketService from './components/MarketService'
import SelectMarketServiceModal from './components/SelectMarketServiceModal'
import AddressModal from './components/AddressModal'
import BookingLocationModal from './components/BookingLocationModal'
import BookingLocationDetailsModal from './components/BookingLocationDetailsModal'
import LoadingStep from './components/LoadingStep'
import PriceNotification from './components/PriceNotification'
import AuthModal from '../../sign-up-sign-in-form/AuthModal'
import { isAuthenticated } from '../../../services/Authenticator'

import { useAllMarketServices, useAllMarkets } from '../../../services/GraphQlClientService'
import { gsColors } from '../../../styles/Variables'
 
import { Header2, Text, Header3 } from '../../../styles/Components'
 
const NewWebServiceSection = styled.section`
   width: 100%;
   display: flex;
   justify-content: center;
   align-items: flex-start;
 
   .service-container {
     width: 100%;
     max-width: 1263px;
     display: flex;
     flex-wrap: wrap;
     justify-content: space-between;
 
     ${mq('max', '1254px')} {
       max-width: 834px;
     }
 
     ${mq('max', '849px')} {
       display: none;
     }
   }
 `
 
const NewMobileServiceSection = styled.section`
   display: none;
 
   ${mq('max', '849px')} {
     display: flex;
     flex-direction: column;
     width: 100%;
   }
 `
 
const TwoHeader = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   width: 100%;
   padding-top: 65px;

   ${mq('max', '768px')} {
     padding: 24px 16px 0;
   }
 
   h2 {
     max-width: 800px;
 
     ${mq('max', '768px')} {
       font-size: 28px !important;
     }
   }
 
   p {
     max-width: 740px;

     ${mq('max', '768px')} {
       font-size: 14px !important;
       font-size: 14px;
       line-height: 20px;
       letter-spacing: 0.56px;
     }
   }
 `

const LocationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  max-width: 480px;
  margin: 0 auto 32px;
  padding: 11px 12px;
  background: none;
  border: 1px solid ${gsColors.sand};

  ${mq('min', '768px')} {
    padding: 16px 12px;
  }
  cursor: pointer;
  font-family: Euclid;
  font-size: 16px;
  color: ${gsColors.black};

  .location-label {
    min-width: 0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .market-name {
    color: ${gsColors.cobalt};
    font-weight: 600;
  }

  .location-caret {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`

// Service-type filter chips that render on their own second row (lowercased).
const SECOND_ROW_SERVICE_TYPES = ['weddings', 'events']

const FilterSection = styled.div`
   display: none;

   ${mq('max', '849px')} {
     margin: -2px 0px 40px;
     display: flex;
     flex-direction: column;
     align-items: center;
     gap: 10px;
   }

   .filter-row {
     width: 100%;
     display: flex;
     flex-wrap: wrap;
     justify-content: center;
     align-items: center;
     gap: 10px;
   }

   button {
     cursor: pointer;
     background-color: white;
     display: flex;
     padding: 12px 15px;
     justify-content: center;
     align-items: center;
     color: ${gsColors.black};
     border-radius: 1px;
     border: 1px solid ${gsColors.lightTangerine};
     text-align: center;
     font-family: Euclid;
     font-size: 14px;
     font-weight: 600;
     line-height: 18px;
     transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

     &:hover:not(.selected) {
       border-color: ${gsColors.cobalt};
       color: ${gsColors.cobalt};
     }

     &.selected {
       background-color: ${gsColors.black};
       color: #fff;
       border-color: ${gsColors.black};
     }
   }
 `
 
const ServiceTypeDiv = styled.div`
   width: 100%;
   max-width: 405px;
   margin-bottom: 111px;
 
   ${mq('max', '849px')} {
     max-width: initial;
     margin-bottom: 57px;
   }
 
   .service-title {
     font-family: Garnett !important;
 
     ${mq('max', '768px')} {
       font-size: 22px !important;
     }
   }
 
   .list-container {
     display: flex;
     flex-direction: column;
     align-items: center;
     gap: 24px;
 
     ${mq('max', '849px')} {
       gap: 20px;
     }
   }
 `
 
const Two = ({
  state: {
    address,
    market,
    serviceRequests = [],
    isDesktop,
    displayedMarketService,
    isMarketServiceModalOpen,
    scrollToServiceType,
    straightToService,
    dayViewBooking,
  },
  dispatch,
  dispatchValueAtKeyPath,
  setIsStepValid,
  moveToNextStep,
  nextStep,
  selectMarketService,
  deselectMarketService,
  me,
}) => {
  // address modal
  const [isAddressModalOpen, setAddressModalOpen] = useState(false)
  const [serviceFilter, setServiceFilter] = useState('All')
  const [displayPriceModal, setDisplayPriceModal] = useState(false)
  const openAddressModal = () => setAddressModalOpen(true)
  const closeAddressModal = () => setAddressModalOpen(false)

  const dayViewEnabled = useDayViewBooking(dayViewBooking)
  const [isLocationDetailsOpen, setLocationDetailsOpen] = useState(false)
  // The location-details modal's "Sign in" link hands off to this modal so the details modal can close
  // first — react-modal unmounts its children on close, so the auth modal has to live one level up.
  const [isAuthModalOpen, setAuthModalOpen] = useState(false)

  const [isLocationModalOpen, setLocationModalOpen] = useState(false)
  const markets = useAllMarkets()
  const enabledMarketIds = (markets || [])
    .filter((m) => m.isEnabled)
    .map((m) => m.id)
  const addresses = me?.addresses?.filter(
    (a) => !!a.marketId && enabledMarketIds.includes(a.marketId)
  )
  // whether the current open of the location modal committed a change — a close
  // without one is a dismissal (tracked so opened/changed/dismissed reconcile)
  const locationCommittedRef = useRef(false)
  const openLocationModal = () => {
    locationCommittedRef.current = false
    trackBookingLocationOpened(market)
    setLocationModalOpen(true)
  }
  const closeLocationModal = () => {
    if (isLocationModalOpen && !locationCommittedRef.current) {
      trackBookingLocationDismissed({ market, straightToService })
    }
    setLocationModalOpen(false)
  }
  const onCommitLocation = ({ market: nextMarket, address: nextAddress, method }) => {
    locationCommittedRef.current = true
    if (nextMarket?.id !== market?.id) {
      trackBookingMarketChanged({ from: market, to: nextMarket, method })
    }
    dispatch({ market: nextMarket, address: nextAddress })
  }
 
  const toggleMobileFilter = ({ target: { value } }) => {
    if (value !== serviceFilter) {
      trackBookingServiceFilterChanged({
        serviceType: value,
        previousServiceType: serviceFilter,
        market,
        straightToService
      })
    }
    setServiceFilter(value)
  }
 
  // market service modal
  const setDisplayedMarketService = (displayedMarketService) =>
    dispatch({ displayedMarketService })
  const setMarketServiceModalOpen = (isMarketServiceModalOpen) =>
    dispatch({ isMarketServiceModalOpen })
 
  // the modal reports its close with what the client had configured; a close
  // that didn't add the service is tracked as a back-out
  const openMarketServiceModal = (marketService) => {
    setDisplayedMarketService(marketService)
    setMarketServiceModalOpen(true)
 
    // track interaction w/ service
    trackSelectService(market, marketService)
  }
  const closeMarketServiceModal = ({ added = false, instances, addOnCount } = {}) => {
    if (isMarketServiceModalOpen && !added && displayedMarketService && !displayedMarketService.service?.isInformational) {
      trackBookingServiceModalClosed({
        marketService: displayedMarketService,
        market,
        instances,
        addOnCount,
        straightToService
      })
    }
    setMarketServiceModalOpen(false)
  }
 
  // close modal, dispatch updated address / market, move on to next step if no new market
  const onSubmitAddress = (updatedMarket, updatedAddress) => {
    closeAddressModal()
 
    // send event up to tag manager
    sendServicesAddedToGoogleTagManager()
 
    if (updatedMarket.id !== market.id) {
      // save address and new market to state
      dispatch({ address: updatedAddress, market: updatedMarket })
    } else {
      // save address to state and move to next step
      dispatch({ address: updatedAddress, step: nextStep })
    }
  }
 
  // load selected market and market services
  const allMarketServices = useAllMarketServices(market?.id)
 
  // load available market services
  const marketServicesForMarket = _(allMarketServices)
    .filter((ms) => ms.marketId === market?.id)
    .sortBy([
      (ms) => ms.service.serviceType.priority,
      (ms) => ms.service.priority,
    ])
    .value()
 
  // track services display
  useEffect(() => {
    if (!marketServicesForMarket?.length) {
      return
    }
 
    trackViewServiceList(market, marketServicesForMarket)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify([marketServicesForMarket, market])])

  // The market whose notice is on screen right now, or null. Marking a market
  // seen and showing its notice happen in the same pass, so without this a
  // re-run of the effect for the same market would read its own write back and
  // close the modal it just opened.
  const openPriceNoticeMarketIdRef = useRef(null)

  // Show the all-fees-included notice the first time a client prices a market
  // whose priceDisplay isn't 'base', then never again for that market on this
  // device. The market is recorded as soon as the notice opens rather than when
  // it is dismissed, so abandoning or refreshing the page doesn't re-show it.
  useEffect(() => {
    const marketId = market?.id

    if (!marketId) {
      // The market can blink out mid-change; drop the open-notice record with it
      // so coming back to the same market is judged on the stored seen list
      // rather than on a stale "already open" ref.
      openPriceNoticeMarketIdRef.current = null
      setDisplayPriceModal(false)
      return
    }

    // An explicit non-base priceDisplay, not merely "not the string 'base'" — a
    // market that hasn't reported how it prices yet must not flash the notice
    // and burn its once-per-market record before we know it needs one. Every
    // market the API serves carries the field, so nothing is suppressed by
    // waiting for it.
    const priceDisplay = market.priceDisplay
    const isAlreadyOpenForThisMarket = openPriceNoticeMarketIdRef.current === marketId
    const shouldShow =
      !!priceDisplay &&
      priceDisplay.toLowerCase() !== 'base' &&
      (isAlreadyOpenForThisMarket || !hasSeenPriceNotification(marketId))

    if (shouldShow) {
      markPriceNotificationSeen(marketId)
    }

    openPriceNoticeMarketIdRef.current = shouldShow ? marketId : null
    setDisplayPriceModal(shouldShow)
    // priceDisplay is a dependency as well as the id: a market can arrive from
    // persisted booking state before the API fills in how it prices, and the
    // notice has to react when it does rather than stay stuck open or shut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market?.id, market?.priceDisplay])

  // handle scrolling to service type if provided
  const serviceTypeRef = useRef(null)
 
  if (serviceTypeRef.current) {
    serviceTypeRef.current.scrollIntoView({ scrollBehavior: 'smooth' })
  }
 
  if (!(market && allMarketServices)) {
    return <LoadingStep />
  }
 
  // group for display
  const marketServicesByServiceType = groupBy(
    marketServicesForMarket,
    'service.serviceType.name'
  )
 
  //sort by priority
  const sortedMarketServicesByServiceType = _.mapValues(
    marketServicesByServiceType,
    (ms) => _.sortBy(ms, 'service.priority')
  )

  const [primaryServiceTypes, secondRowServiceTypes] = _.partition(
    Object.keys(sortedMarketServicesByServiceType),
    (name) => !SECOND_ROW_SERVICE_TYPES.includes(name.trim().toLowerCase())
  )

  // Fall back to "All" when the persisted filter names a service type the
  // current market doesn't offer (e.g. after switching markets).
  const activeFilter =
    serviceFilter === 'All' || sortedMarketServicesByServiceType[serviceFilter]
      ? serviceFilter
      : 'All'
 
  const marketServicesByServiceId = keyBy(marketServicesForMarket, 'service.id')

  // in case of market change, filter out any selected services which don't exist here
  if (servicesUnavailableInMarket(serviceRequests, marketServicesForMarket).length) {
    return dispatchValueAtKeyPath(
      'serviceRequests',
      serviceRequests.filter(
        (sr) => marketServicesByServiceId[sr?.serviceId]
      )
    )
  }
 
  /**
    * sendServicesAddedToGoogleTagManager: sends event to google tag manager containing services in cart and total price
    *
    * @returns
    */
  const sendServicesAddedToGoogleTagManager = () => {
    if (!window.dataLayer) {
      return
    }
 
    let servicesAdded = serviceRequests.map(
      ({ serviceId }) => marketServicesByServiceId[serviceId]?.service?.name
    )
    let servicesSubtotal = sum(
      serviceRequests.map(
        ({ serviceId }) => marketServicesByServiceId[serviceId]?.price
      )
    )
 
    window.dataLayer.push({
      event: 'addServicesToCart',
      servicesAdded,
      servicesSubtotal,
    })
  }
 
  // step is valid if we have at least one service selected
  setIsStepValid(!!serviceRequests.length, 'Select Date & Time', () => {
    if (dayViewEnabled) {
      if (address.zip && address.type) {
        sendServicesAddedToGoogleTagManager()
        return moveToNextStep()
      }
      return setLocationDetailsOpen(true)
    }

    if (!address.zip) {
      return openAddressModal()
    }

    // send event up to tag manager
    sendServicesAddedToGoogleTagManager()

    return moveToNextStep()
  })
 
  return (
    <>
      {straightToService && (
        <BookingLocationModal
          isOpen={isLocationModalOpen}
          close={closeLocationModal}
          currentMarket={market}
          address={address}
          addresses={addresses}
          markets={markets || []}
          me={me}
          isDesktop={isDesktop}
          serviceRequests={serviceRequests}
          allMarketServices={allMarketServices}
          onCommit={onCommitLocation}
        />
      )}
      <SelectMarketServiceModal
        isOpen={isMarketServiceModalOpen}
        close={closeMarketServiceModal}
        market={market}
        marketService={displayedMarketService}
        select={selectMarketService}
        deselect={deselectMarketService}
        isDesktop={isDesktop}
      />
      {/* if still required, collect the address from the user and move next */}
      <AddressModal
        currentMarket={market}
        serviceRequests={serviceRequests}
        allMarketServices={allMarketServices}
        isOpen={isAddressModalOpen}
        close={closeAddressModal}
        onSubmit={onSubmitAddress}
        isDesktop={isDesktop}
      />
      <BookingLocationDetailsModal
        isOpen={isLocationDetailsOpen}
        close={() => setLocationDetailsOpen(false)}
        isDesktop={isDesktop}
        currentMarket={market}
        address={address}
        market={market}
        serviceRequests={serviceRequests}
        allMarketServices={allMarketServices}
        onSignIn={() => { setLocationDetailsOpen(false); setAuthModalOpen(true) }}
        onSubmit={(updatedMarket, updatedAddress) => {
          setLocationDetailsOpen(false)
          sendServicesAddedToGoogleTagManager()
          if (updatedMarket && updatedMarket.id !== market.id) {
            dispatch({ address: updatedAddress, market: updatedMarket })
          } else {
            dispatch({ address: updatedAddress, step: nextStep })
          }
        }}
      />
      <AuthModal
        isOpen={isAuthModalOpen}
        close={() => { dispatch({ isAuthenticated: isAuthenticated() }); setAuthModalOpen(false) }}
        isDesktop={isDesktop}
      />
      <PriceNotification
        isOpen={displayPriceModal}
        close={() => {
          trackPriceNotificationDismissed({ market, straightToService })
          openPriceNoticeMarketIdRef.current = null
          setDisplayPriceModal(false)
        }}
      />
      <TwoHeader>
        <Header2 margin="0 0 16px" lineHeight="138%" align="center">
           Which services would you like to book?
        </Header2>
        <Text fontSize={18} lineHeight="26px" margin="0 0 32px">
           Make a selection to start. You&apos;ll be assigned one pro per
           service. Multiple services will be performed at once unless otherwise
           specified.
        </Text>
      </TwoHeader>
      {straightToService && (
        <LocationButton type="button" onClick={openLocationModal} aria-haspopup="dialog">
          <span className="location-label">
            {address?.street ? (
              <>Glam at <span className="market-name">{[address.street, address.apartment].filter(Boolean).join(', ')}</span></>
            ) : market ? (
              <>Glam in <span className="market-name">{market.name}</span></>
            ) : (
              <span className="market-name">Select your location</span>
            )}
          </span>
          <img className="location-caret" src="/images/chevron-right-cobalt.svg" alt="" aria-hidden="true" />
        </LocationButton>
      )}
      <FilterSection>
        <div className="filter-row">
          <button
            value="All"
            onClick={toggleMobileFilter}
            className={activeFilter === 'All' ? 'selected' : ''}
          >
             All
          </button>
          {primaryServiceTypes.map((t) => (
            <button
              className={activeFilter === t ? 'selected' : ''}
              value={t}
              key={t}
              onClick={toggleMobileFilter}
            >
              {t}
            </button>
          ))}
        </div>
        {secondRowServiceTypes.length > 0 && (
          <div className="filter-row">
            {secondRowServiceTypes.map((t) => (
              <button
                className={activeFilter === t ? 'selected' : ''}
                value={t}
                key={t}
                onClick={toggleMobileFilter}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </FilterSection>
      <NewMobileServiceSection>
        {activeFilter === 'All' ? (
          <>
            {marketServicesForMarket.filter((ms) => ms.isPopular).length ? (
              <ServiceTypeDiv>
                <Header3
                  className="service-title"
                  fontSize={22}
                  color={gsColors.cobalt}
                  weight={750}
                  margin="0 0 29px"
                >
                    Popular
                </Header3>
                <div className="list-container">
                  {marketServicesForMarket
                    .filter((ms) => ms.isPopular)
                    .map((marketService, i) => (
                      <MarketService
                        tabindex={i}
                        key={marketService.id}
                        marketService={marketService}
                        onClick={() => openMarketServiceModal(marketService)}
                        popular={true}
                      />
                    ))}
                </div>
              </ServiceTypeDiv>
            ) : null}
            {map(
              sortedMarketServicesByServiceType,
              (marketServices, serviceTypeName) => (
                <ServiceTypeDiv key={serviceTypeName}>
                  {serviceTypeName &&
                     scrollToServiceType &&
                     serviceTypeName.toLowerCase() ===
                       scrollToServiceType.toLowerCase() && (
                    <ScrollIntoView ref={serviceTypeRef} />
                  )}
                  <Header3
                    className="service-title"
                    fontSize={28}
                    align="left"
                    weight={750}
                    color={gsColors.black}
                    lineHeight="138%"
                    margin="0 0 29px"
                  >
                    {serviceTypeName}
                  </Header3>
                  <div className="list-container">
                    {marketServices.map((marketService) => (
                      <MarketService
                        key={marketService.id}
                        marketService={marketService}
                        onClick={() => openMarketServiceModal(marketService)}
                        popular={isDesktop && marketService.isPopular}
                      />
                    ))}
                  </div>
                </ServiceTypeDiv>
              )
            )}
          </>
        ) : (
          <>
            <ServiceTypeDiv>
              {activeFilter &&
                 scrollToServiceType &&
                 activeFilter.toLowerCase() ===
                   scrollToServiceType.toLowerCase() && (
                <ScrollIntoView ref={serviceTypeRef} />
              )}
              <Header3
                className="service-title"
                fontSize={28}
                align="left"
                weight={750}
                color={gsColors.black}
                lineHeight="138%"
                margin="0 0 36px"
              >
                {activeFilter}
              </Header3>
              <div className="list-container">
                {sortedMarketServicesByServiceType[activeFilter].map(
                  (marketService) => (
                    <MarketService
                      key={marketService.id}
                      marketService={marketService}
                      onClick={() => openMarketServiceModal(marketService)}
                      popular={isDesktop && marketService.isPopular}
                    />
                  )
                )}
              </div>
            </ServiceTypeDiv>
          </>
        )}
      </NewMobileServiceSection>
      <NewWebServiceSection>
        <div className="service-container">
          {map(
            sortedMarketServicesByServiceType,
            (marketServices, serviceTypeName) => (
              <ServiceTypeDiv key={serviceTypeName}>
                {serviceTypeName &&
                   scrollToServiceType &&
                   serviceTypeName.toLowerCase() ===
                     scrollToServiceType.toLowerCase() && (
                  <ScrollIntoView ref={serviceTypeRef} />
                )}
                <Header3
                  className="service-title"
                  fontSize={28}
                  align="center"
                  weight={750}
                  color={gsColors.black}
                  lineHeight="138%"
                  margin="0 0 36px"
                >
                  {serviceTypeName}
                </Header3>
                <div className="list-container">
                  {marketServices.map((marketService) => (
                    <MarketService
                      market={market}
                      key={marketService.id}
                      marketService={marketService}
                      onClick={() => openMarketServiceModal(marketService)}
                      popular={isDesktop && marketService.isPopular}
                    />
                  ))}
                </div>
              </ServiceTypeDiv>
            )
          )}
        </div>
      </NewWebServiceSection>
    </>
  )
}
 
Two.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  dispatchValueAtKeyPath: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
  selectMarketService: PropTypes.func.isRequired,
}
 
export default Two
 
const ScrollIntoView = forwardRef((ignored, ref) => <div ref={ref}></div>)
 
